import { useQuery } from "@tanstack/react-query";
import type { Exit, Figure, ListBox, ListFacet, Read, RowAct } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { coursesApi, type Course, type CourseStatus } from "@/lib/api/courses";
import { NONE } from "@/lib/course-form";
import { isHistoricalTerm } from "@/lib/term-state";

const IMAM = { system: "imam" } as const;

export interface SectionFilters {
  query: string;
  programId: string;
  /** The term filter, and whether that term is closed. */
  term: { id: string; name: string; closed: boolean } | null;
  /** `?status=`: "archived" is the archive; "draft" or "published" narrow the active list. */
  status: string;
}

export const SECTIONS_COPY = {
  label: "Sections",
  archivedLabel: "Archived sections",
  job: "Find a section, then open its record.",
  find: "Find a section by title or code",
  failed: "Couldn't read the sections",
  failedDetail: "This isn't a record that no sections exist.",
  stale: "Couldn't refresh. A later change isn't shown.",
  none: "No sections yet.",
  noneSub: "Add the first section to begin the catalog.",
  noneArchived: "No archived sections.",
  noneArchivedSub: "An archived section keeps its academic record here.",
  retained: "Archived sections keep their academic records here, outside active teaching and learning workspaces.",
  handoff: "Admissions carried a student here without guessing a course. Choose a section to build the roster plan.",
  count: (n: number) => `${n} ${n === 1 ? "section" : "sections"}`,
  archivedCount: (n: number) => `${n} archived ${n === 1 ? "section" : "sections"}`,
  closedTerm: (term: string) => `${term} is a closed term`,
  closedSub: "Its sections are read in Course History.",
  open: "Open section",
  openRoster: "Open roster",
  openHistory: "Open in Course History",
  noProgram: "No program",
  noTerm: "No term",
  programsFailed: "Couldn't read the programs.",
  termsFailed: "Couldn't read the terms.",
  add: "Create course",
  intake: "Bring in from Classroom",
  enrolled: (n: number, capacity: number | null) => `${n} enrolled${capacity == null ? " · no capacity limit" : ` · capacity ${capacity}`}`,
  status: { draft: "Draft", published: "Published", archived: "Archived" } satisfies Record<CourseStatus, string>,
} as const;

interface Shown {
  rows: Course[];
  hidden: number;
  historical: number;
}

function matches(course: Course, filters: SectionFilters): boolean {
  const query = filters.query.trim().toLowerCase();
  // The server's own search: title or code (courses.py list_courses).
  if (query && !course.title.toLowerCase().includes(query) && !course.code.toLowerCase().includes(query)) return false;
  if (filters.programId !== NONE && course.program_id !== filters.programId) return false;
  return true;
}

/** The rows the tools leave, how many of the view they hide, and the closed-term sections outside the active view. */
export function shownSections(all: Course[], filters: SectionFilters): Shown {
  const archived = filters.status === "archived";
  const narrowed = all.filter((course) => matches(course, filters));
  const historical = narrowed.filter((course) => course.status !== "archived" && isHistoricalTerm(course.semester_status)).length;
  if (!archived && filters.term?.closed) {
    // A closed term's sections are read in Course History, never as live rows;
    // the find, program and status still narrow them, and the head says what they hide.
    const inTerm = all.filter((course) => course.status !== "archived" && course.semester_id === filters.term!.id);
    const rows = inTerm.filter((course) => matches(course, filters) && (filters.status === NONE || course.status === filters.status));
    return { rows, hidden: inTerm.length - rows.length, historical };
  }
  const view = all.filter((course) => (archived ? course.status === "archived" : course.status !== "archived" && !isHistoricalTerm(course.semester_status)));
  const rows = view.filter(
    (course) =>
      matches(course, filters) &&
      (filters.term === null || course.semester_id === filters.term.id) &&
      (archived || filters.status === NONE || course.status === filters.status),
  );
  return { rows, hidden: view.length - rows.length, historical };
}

/**
 * The register's one read: every section, archived ones included. A refused
 * read withdraws its rows; a failed refresh keeps the last rows as stale (R2).
 */
export function useSectionsRead(enabled: boolean): Read<Course[]> {
  const courses = useQuery({
    queryKey: ["courses", { include_archived: true }],
    queryFn: () => coursesApi.list({ include_archived: true }),
    enabled,
  });
  const refused = courses.error instanceof ApiError && [401, 403, 404].includes(courses.error.status);
  if (courses.data !== undefined && !refused)
    return courses.isError
      ? {
          status: "stale",
          last: courses.data,
          readAt: new Date(courses.dataUpdatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
          sentence: SECTIONS_COPY.stale,
          refresh: () => void courses.refetch(),
        }
      : { status: "ready", value: courses.data, readAt: new Date(courses.dataUpdatedAt).toISOString() };
  return courses.isError
    ? { status: "failed", sentence: SECTIONS_COPY.failed, detail: SECTIONS_COPY.failedDetail, retry: () => void courses.refetch() }
    : { status: "loading" };
}

/** A filter whose options are a read of their own waits while it reads and says a failed read beside itself (R1). */
export function facetRead(query: { isPending: boolean; isError: boolean; refetch: () => unknown }, sentence: string): ListFacet["read"] {
  if (query.isError) return { status: "failed", sentence, retry: () => void query.refetch() };
  return query.isPending ? { status: "loading" } : undefined;
}

export function sectionsList({
  read,
  filters,
  archived,
  rowAct,
  historyHref,
  handoff,
  onQuery,
  facets,
  exits,
}: {
  read: Read<Course[]>;
  filters: SectionFilters;
  archived: boolean;
  rowAct: (course: Course) => RowAct;
  /** Course History, narrowed to a term. */
  historyHref: (termId: string) => string;
  handoff: boolean;
  /** The find query as the person types it. */
  onQuery: (query: string) => void;
  /** Program, term and status: the List's own filters (R6). */
  facets: ListFacet[];
  /** Add section and Bring in from Classroom: the register's ways to other pages, never acts (SD-7). */
  exits?: [Exit, Exit];
}): ListBox<Course[], Course> {
  const copy = SECTIONS_COPY;
  const closedTerm = !archived && filters.term?.closed ? filters.term : null;
  // An empty institution, or an empty archive, is the read's own sentence (R1).
  const answered: Read<Course[]> =
    read.status === "ready" && !read.value.some((course) => (archived ? course.status === "archived" : true))
      ? archived
        ? { status: "empty", sentence: copy.noneArchived, sub: copy.noneArchivedSub }
        : { status: "empty", sentence: copy.none, sub: copy.noneSub }
      : read;
  const shown = (all: Course[]) => shownSections(all, filters);
  const value = answered.status === "ready" ? answered.value : answered.status === "stale" ? answered.last : undefined;
  return {
    kind: "list",
    id: "sections",
    job: copy.job,
    label: archived ? copy.archivedLabel : copy.label,
    owner: IMAM,
    fold: "never",
    exits,
    read: answered,
    detail: (all) => (archived ? copy.archivedCount : copy.count)(shown(all).rows.length),
    openDetail: () => [handoff ? copy.handoff : null, archived ? copy.retained : null, closedTerm ? copy.closedSub : null].filter(Boolean).join(" ") || undefined,
    // The head's state word is the count (A19), as the drawn register head says it once (CASE-001).
    state: (all) => {
      if (closedTerm) return { class: "quiet", word: copy.closedTerm(closedTerm.name) };
      const n = shown(all).rows.length;
      return { class: "quiet", word: archived ? copy.archivedCount(n) : copy.count(n) };
    },
    figures: (all): Figure[] => {
      const { rows, historical } = shown(all);
      if (archived)
        return [
          { value: rows.reduce((total, course) => total + course.enrolled_count, 0), unit: "enrolled" },
          { value: rows.filter((course) => isHistoricalTerm(course.semester_status)).length, unit: "in closed terms" },
        ];
      return [
        { value: rows.filter((course) => course.status === "published").length, unit: "published" },
        { value: rows.filter((course) => course.status === "draft").length, unit: "draft" },
        { value: historical, unit: "in closed terms" },
      ];
    },
    rows: (all) => [...shown(all).rows].sort((a, b) =>
      // Keep each term together; unscheduled drafts precede dated offerings.
      (a.semester_id === null ? 0 : 1) - (b.semester_id === null ? 0 : 1)
      || (b.semester_start_date ?? "").localeCompare(a.semester_start_date ?? "")
      || (a.semester_name ?? "").localeCompare(b.semester_name ?? "")
      || (a.semester_id ?? "").localeCompare(b.semester_id ?? "")),
    // The kit says "N not shown by this filter" in the head.
    find: { placeholder: copy.find, onQueryChange: onQuery, facets, hidden: value === undefined ? undefined : shown(value).hidden },
    row: {
      key: (course) => course.id,
      group: (course) => `${course.semester_name ?? copy.noTerm}${isHistoricalTerm(course.semester_status) ? " · closed" : ""}`,
      title: (course) => `${course.code} · ${course.title}`,
      // Only a row that differs is marked: a draft among published sections.
      state: (course) => (course.status === "draft" && !archived ? { class: "quiet", word: copy.status.draft } : null),
      facts: (course) =>
        // The CRN tells two sections of one course apart, as the band and rail do.
        [course.program_name ?? copy.noProgram, course.crn ? `CRN ${course.crn}` : null, copy.enrolled(course.enrolled_count, course.capacity)]
          .filter(Boolean)
          .join(" · "),
      act: (course) => (closedTerm ? { does: "exit", verb: copy.openHistory, to: historyHref(closedTerm.id) } : rowAct(course)),
    },
  };
}
